import api from '../api/client';
import React, { useRef, useState, useEffect, useMemo } from 'react';
import ProTable from '@ant-design/pro-table';
import {
    Button,
    Space,
    Tag,
    Typography,
    message,
    ConfigProvider,
    Card,
    Row,
    Col,
    Statistic,
    Badge,
    Alert,
    Tabs,
    Drawer,
    Descriptions,
    Table,
    Divider,
    Popover,
    Modal,
    Form,
    Input,
    DatePicker,
    Popconfirm,
    Select,
    Tooltip,
    InputNumber,
    Spin,
    Collapse
} from 'antd';
import {
    EyeOutlined,
    ShoppingOutlined,
    MedicineBoxOutlined,
    BarChartOutlined,
    InboxOutlined,
    CloseOutlined,
    DollarOutlined,
    RollbackOutlined,
    EditOutlined,
    DownloadOutlined,
    PlusOutlined,
    DeleteOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import moment from 'moment';
import config from '../config';
import { formatKES } from '../utils/currency';
import {
    addPayment,
    exportReceipts,
    exportRestocks,
    fetchInventoryStats,
    fetchInventorySummary,
    fetchItems,
    fetchPaymentReceipts,
    fetchRestocks,
    fetchSuppliers,
    fetchUsers,
    reverseConsumption,
    updateInventoryHeader,
    updateItem
} from '../api';
import { useSelector } from 'react-redux';
import * as XLSX from 'xlsx'; // optional, for Excel export

import { useCallback } from 'react';
import { groupAccountOptions } from '../utils/account';
import { formatFullDate } from '../utils/dateUtils';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;
// const { Step } = Steps;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const StockManagementPage = () => {
    const actionRef = useRef();
    // Add a ref to trigger ProTable reload
    const consumptionActionRef = useRef();
    const [activeTab, setActiveTab] = useState('restocks');
    // const [stats, setStats] = useState(null);
    const [detailsVisible, setDetailsVisible] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [supplierMap, setSupplierMap] = useState({});
    const [userMap, setUserMap] = useState({});

    const [identifiers, setIdentifiers] = useState([]);
    const [loadingIdentifiers, setLoadingIdentifiers] = useState(false);
    const [stats, setStats] = useState({ total_purchases: 0, total_paid: 0, total_balance: 0, overdue_count: 0 });
    const [statsFilters, setStatsFilters] = useState({});
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [paymentModalVisible, setPaymentModalVisible] = useState(false);
    const [editForm] = Form.useForm();
    const [itemsForm] = Form.useForm();
    const [paymentForm] = Form.useForm();
    const [itemEditVisible, setItemEditVisible] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [itemEditForm] = Form.useForm();
    const account = useSelector((state) => state.account.user);
    const [selectedRows, setSelectedRows] = useState([]);
    const [users, setUsers] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [items, setItems] = useState([]);
    const [receipts, setReceipts] = useState([]);
    const [loadingReceipts, setLoadingReceipts] = useState(false);
    const [paidAccounts, setPaidAccounts] = useState([]);
    const [allItems, setAllItems] = useState([]);
    // Animal report state
    const [animalReportData, setAnimalReportData] = useState([]);
    const [animalCategories, setAnimalCategories] = useState([]);
    const [animalsList, setAnimalsList] = useState([]);
    const [consumptionFilters, setConsumptionFilters] = useState({
        dateRange: null,
        type: null,
        category: null,
        item_id: null
    });
    const [consumptionData, setConsumptionData] = useState([]);
    const [consumptionLoading, setConsumptionLoading] = useState(false);
    const [consumptionTotal, setConsumptionTotal] = useState(0);
    const [balance, setBalance] = useState(0);

    // Filter options
    const [consumptionTypes, setConsumptionTypes] = useState([]);
    const [consumptionCategories, setConsumptionCategories] = useState([]);
    const [consumptionItems, setConsumptionItems] = useState([]);
    const [inventoryItems, setInventoryItems] = useState([]);
    // const [editModalVisible, setEditModalVisible] = useState(false);
    const [itemsModalVisible, setItemsModalVisible] = useState(false);
    // Additional state
    const [consumptionStats, setConsumptionStats] = useState({ total_quantity: 0, total_cost: 0, record_count: 0 });
    // const [animalsList, setAnimalsList] = useState([]);

    // Add to your Inventory component
    const [reversalModalVisible, setReversalModalVisible] = useState(false);
    const [selectedPurchase, setSelectedPurchase] = useState(null);
    const [reversalForm] = Form.useForm();
    const [reversing, setReversing] = useState(false);

    const [identifiersLoading, setIdentifiersLoading] = useState(false);
    const [animalIdentifiers, setAnimalIdentifiers] = useState([]);
    const [siloIdentifiers, setSiloIdentifiers] = useState([]);

    // Function to load identifiers for the selected record
    const loadIdentifiersForRecord = async (record) => {
        setIdentifiersLoading(true);
        try {
            const requestBody = {};

            // Handle animal_ids
            if (record.animal_ids) {
                let animalIds = record.animal_ids;
                if (typeof animalIds === 'string') {
                    try {
                        animalIds = JSON.parse(animalIds);
                    } catch (e) {
                        animalIds = [];
                    }
                }
                if (Array.isArray(animalIds) && animalIds.length > 0) {
                    requestBody.animal_ids = animalIds;
                    requestBody.type_slug = record.category;
                }
            }

            // Handle silo_ids
            if (record.silo_ids) {
                let siloIds = record.silo_ids;
                if (typeof siloIds === 'string') {
                    try {
                        siloIds = JSON.parse(siloIds);
                    } catch (e) {
                        siloIds = [];
                    }
                }
                if (Array.isArray(siloIds) && siloIds.length > 0) {
                    requestBody.silo_ids = siloIds;
                }
            }

            if (Object.keys(requestBody).length === 0) {
                setAnimalIdentifiers([]);
                setSiloIdentifiers([]);
                return;
            }

            const response = await fetch(`/identifiers/batch`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });

            const data = await response.json();
            // console.log("show ident", data)
            setAnimalIdentifiers(data.animals || []);
            setSiloIdentifiers(data.silos || []);
        } catch (error) {
            console.error('Error loading identifiers:', error);
            setAnimalIdentifiers([]);
            setSiloIdentifiers([]);
        } finally {
            setIdentifiersLoading(false);
        }
    };

    const handleReversePurchase = (record) => {
        setSelectedPurchase(record);
        setReversalModalVisible(true);
    };

    const confirmReversePurchase = async () => {
        try {
            const values = await reversalForm.validateFields();
            setReversing(true);

            const response = await api.post(`/reverse-purchase/${selectedPurchase.id}`, {
                reason: values.reason,
                reason_detail: values.reason_detail,
                reversed_by: account?.id,
                reversed_by_name: account?.firstname
            });

            if (response.data.success) {
                message.success(`Purchase ${selectedPurchase.transactionId} reversed successfully`);
                setReversalModalVisible(false);
                setSelectedPurchase(null);
                reversalForm.resetFields();

                // Refresh the table
                actionRef.current?.reload();
                loadStats();
            }
        } catch (error) {
            console.error('Error reversing purchase:', error);
            message.error(error.response?.data?.error || 'Failed to reverse purchase');
        } finally {
            setReversing(false);
        }
    };

    // Fetch all items for editing
    const fetchAllItems = async () => {
        try {
            const response = await api.get(`/api/items`);
            setAllItems(response.data);
        } catch (error) {
            console.error('Error fetching items1:', error);
        }
    };

    useEffect(() => {
        fetchSuppliers();
        fetchAllItems();
        loadStats();
    }, [statsFilters]);

    // Fetch consumptions with filters
    const fetchConsumptionsWithFilters = useCallback(
        async (sort = {}) => {
            setConsumptionLoading(true);

            const queryParams = {};

            if (consumptionFilters.dateRange?.length === 2) {
                queryParams.startDate = consumptionFilters.dateRange[0].format('YYYY-MM-DD');
                queryParams.endDate = consumptionFilters.dateRange[1].format('YYYY-MM-DD');
            }
            if (consumptionFilters.type) queryParams.type = consumptionFilters.type;
            if (consumptionFilters.category) queryParams.category = consumptionFilters.category;
            if (consumptionFilters.item_id) queryParams.item_id = parseInt(consumptionFilters.item_id, 10);
            if (consumptionFilters.animal_id) queryParams.animal_id = consumptionFilters.animal_id;

            if (Object.keys(sort).length) {
                const sortField = Object.keys(sort)[0];
                const sortOrder = sort[sortField];
                queryParams.sort = `${sortField}:${sortOrder === 'ascend' ? 'ASC' : 'DESC'}`;
            }

            try {
                const res = await api.get('/api/consumptions-v1', { params: queryParams });
                setConsumptionData(res.data.data);
                setConsumptionTotal(res.data.total);
                return { data: res.data.data, total: res.data.total, success: true };
            } catch (err) {
                console.error('Error fetching consumptions:', err);
                setConsumptionData([]);
                setConsumptionTotal(0);
                return { data: [], total: 0, success: false };
            } finally {
                setConsumptionLoading(false);
            }
        },
        [consumptionFilters]
    );

    // Effect to reload table when filters change (reset to page 1)
    useEffect(() => {
        if (consumptionActionRef.current) {
            consumptionActionRef.current.reloadAndRest?.(); // ProTable method to reset pagination and reload
        }
    }, [consumptionFilters]);

    const fetchConsumptionStats = useCallback(async () => {
        const queryParams = {};

        if (consumptionFilters.dateRange?.length === 2) {
            queryParams.startDate = consumptionFilters.dateRange[0].format('YYYY-MM-DD');
            queryParams.endDate = consumptionFilters.dateRange[1].format('YYYY-MM-DD');
        }
        if (consumptionFilters.type) queryParams.type = consumptionFilters.type;
        if (consumptionFilters.category) queryParams.category = consumptionFilters.category;
        if (consumptionFilters.item_id) queryParams.item_id = consumptionFilters.item_id;
        if (consumptionFilters.animal_id) queryParams.animal_id = consumptionFilters.animal_id;

        try {
            const res = await api.get('/consumptions/stats-v1', { params: queryParams });
            setConsumptionStats(res.data);
        } catch (err) {
            console.error('Error fetching consumption stats:', err);
        }
    }, [consumptionFilters]);

    // Load animals for filter
    useEffect(() => {
        const loadAnimals = async () => {
            try {
                const res = await api.get('/animal-assets/list');
                setAnimalsList(res.data);
            } catch (err) {
                console.error('Error loading animals:', err);
            }
        };
        loadAnimals();
    }, []);

    // Load filter options (types, categories, items)
    useEffect(() => {
        const loadFilterOptions = async () => {
            try {
                const [typesRes, categoriesRes, itemsRes] = await Promise.all([
                    api.get('/items/types'),
                    api.get('/items/categories'),
                    api.get('/items')
                ]);
                setConsumptionTypes(typesRes.data);
                setConsumptionCategories(categoriesRes.data);
                setConsumptionItems(itemsRes.data);
            } catch (err) {
                console.error('Error loading filter options:', err);
            }
        };
        loadFilterOptions();
    }, []);

    // Load static data for filters
    useEffect(() => {
        const loadFilters = async () => {
            try {
                const [supplierData, userData] = await Promise.all([fetchSuppliers(), fetchUsers()]);
                setSuppliers(supplierData);
                setUsers(userData);
            } catch (err) {
                console.error('Error loading suppliers/users:', err);
            }
        };
        loadFilters();
    }, []);

    useEffect(() => {
        const loadSuppliers = async () => {
            try {
                const res = await api.get('/suppliers');
                const map = {};
                res.data.forEach((s) => {
                    map[s.id] = s.name;
                });
                setSupplierMap(map);
            } catch (err) {
                console.error('Error loading suppliers:', err);
            }
        };
        loadSuppliers();
    }, []);

    useEffect(() => {
        const loadUsers = async () => {
            try {
                const res = await api.get('/users');
                const map = {};
                res.data.forEach((s) => {
                    map[s.id] = s.firstname;
                });
                setUserMap(map);
            } catch (err) {
                console.error('Error loading users:', err);
            }
        };
        loadUsers();
    }, []);

    const loadAnimalFilters = async () => {
        try {
            const [cats, animals] = await Promise.all([
                api.get('/animal-assets/list', { params: { type_slug: consumptionFilters?.category } }),
                api.get('/animal-assets/list')
            ]);
            setAnimalCategories(cats.data);
            setAnimalsList(animals.data);
        } catch (err) {
            console.error('Error loading animal filters:', err);
        }
    };

    // Update stats whenever filters change
    useEffect(() => {
        fetchConsumptionStats();
    }, [consumptionFilters]);

    // Load static data for filters
    useEffect(() => {
        const loadFilters = async () => {
            try {
                const [supplierData, userData, itemsData] = await Promise.all([fetchSuppliers(), fetchUsers(), fetchItems()]);
                setSuppliers(supplierData);
                setUsers(userData);
                setItems(itemsData);
            } catch (error) {
                console.error(error);
            }
        };
        loadFilters();
        loadAnimalFilters();
    }, []);

    // Fetch paid-through accounts for the form (used in select options)
    const fetchPaidAccounts = useCallback(async () => {
        try {
            const { data } = await api.get(`/api/paid-through`);
            setPaidAccounts(data);
        } catch (error) {
            console.error('Failed to fetch paid-through accounts', error);
        }
    }, [api]);

    useEffect(() => {
        fetchPaidAccounts();
    }, [fetchPaidAccounts]);

    // Prepare select options (grouped) for the form
    const paidOptions = groupAccountOptions(paidAccounts); // from your utils

    useEffect(() => {
        if (!selectedRecord) return;

        const loadReceipts = async () => {
            try {
                setLoadingReceipts(true);

                const res = await fetchPaymentReceipts({
                    transactionId: selectedRecord.transactionId,
                    id: selectedRecord.id
                });

                // Ensure it's always an array
                setReceipts(Array.isArray(res) ? res : []);
            } catch (err) {
                console.error('Failed to fetch receipts', err);
                setReceipts([]);
            } finally {
                setLoadingReceipts(false);
            }
        };

        loadReceipts();
    }, [selectedRecord]);

    const loadStats = async (filters) => {
        const res = await fetchInventoryStats(filters);
        setStats(res);
    };

    useEffect(() => {
        // fetchStats();
        loadStats();
    }, []);

    // Fetch inventory items for a specific header
    const fetchInventoryItems = async (headerId) => {
        try {
            const response = await api.get(`/inventory-items/${headerId}`);
            setInventoryItems(response.data);
            return response.data;
        } catch (error) {
            console.error('Error fetching inventory items:', error);
            return [];
        }
    };

    // Open items edit modal
    const openItemsModal = async (record) => {
        setSelectedRecord(record);
        // loadIdentifiersForRecord(record)
        const items = await fetchInventoryItems(record.id);
        itemsForm.setFieldsValue({
            items: items.map((item) => ({
                item_id: item.item_id,
                item_name: item.item_name,
                quantity: item.quantity,
                unit_cost: item.unit_cost,
                total_cost: item.total_cost
            }))
        });
        setItemsModalVisible(true);
    };

    // ---- Export functions (CSV/Excel per tab) ----
    const exportTable = (data, filename) => {
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
        XLSX.writeFile(wb, `${filename}.xlsx`);
    };

    const exportRestocksData = async () => {
        const res = await fetch(`/inventory/export?format=json`);
        const data = await res.json();
        exportTable(data, `restocks_${moment().format('YYYYMMDD')}`);
    };

    const exportConsumptionsData = async () => {
        const res = await fetch(`/consumptions/export`);
        const data = await res.json();
        exportTable(data, `consumptions_${moment().format('YYYYMMDD')}`);
    };

    const exportStockData = async () => {
        const res = await fetch(`/items/stock/export`);
        const data = await res.json();
        exportTable(data, `current_stock_${moment().format('YYYYMMDD')}`);
    };

    // View details
    const viewDetails = (record) => {
        setSelectedRecord(record);
        setDetailsVisible(true);
    };

    // Close drawer
    const closeDetails = () => {
        setDetailsVisible(false);
        setSelectedRecord(null);
    };

    const restockColumns = [
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            valueType: 'date',
            sorter: true,
            render: (_, record) => formatFullDate(record.recorded)
        },
        {
            title: '#',
            dataIndex: 'transactionId',
            key: 'transactionId',
            copyable: true,
            width: 150
        },
        {
            title: 'Supplier',
            dataIndex: 'stakeholderId',
            key: 'stakeholderId',
            render: (_, record) => (
                <Link to={`/stakeholder/details/${record.stakeholderId}`}>{supplierMap[record.stakeholderId] || '—'}</Link>
            )
        },
        {
            title: 'Type',
            dataIndex: 'inventoryType',
            key: 'inventoryType',
            filters: true,
            valueEnum: {
                new_stock: { text: 'New Stock', status: 'Success' },
                restock: { text: 'Restock', status: 'Warning' }
            }
        },
        {
            title: 'Items',
            key: 'items',
            render: (_, record) => {
                const items = record.items || [];

                const content = (
                    <div style={{ maxWidth: 300 }}>
                        {items.map((item, i) => (
                            <div key={i} style={{ marginBottom: 8 }}>
                                {item.item_name} × {item.quantity} <br />@ KES {item.unit_cost} = <b>KES {item.total_cost}</b>
                            </div>
                        ))}
                    </div>
                );

                return (
                    <Popover content={content} title="Items" trigger="click">
                        <Button type="link">
                            {items.length} item{items.length !== 1 ? 's' : ''}
                        </Button>
                    </Popover>
                );
            }
        },
        {
            title: 'Total Cost',
            dataIndex: 'totalCost',
            key: 'totalCost',
            sorter: true,
            render: (value) => formatKES(value)
        },
        {
            title: 'Paid (KES)',
            dataIndex: 'paidAmount',
            key: 'paidAmount',
            sorter: true,
            render: (value) => formatKES(value)
        },
        {
            title: 'Balance',
            render: (_, r) => formatKES(r.totalCost - (r.paidAmount || 0)),
            sorter: (a, b) => a.totalCost - a.paidAmount - (b.totalCost - b.paidAmount)
        },
        {
            title: 'Status',
            dataIndex: 'paymentStatus',
            key: 'paymentStatus',
            filters: true,
            valueEnum: {
                paid: { text: 'Paid', status: 'Success' },
                unpaid: { text: 'Unpaid', status: 'Error' },
                partial: { text: 'Partial', status: 'Warning' },
                overpaid: { text: 'Overpaid', status: 'Primary' }
            }
        },

        {
            title: 'Actions',
            key: 'actions',
            fixed: 'right',
            width: 180,
            render: (_, record) => (
                <Space>
                    <Tooltip title="View Details">
                        <Button icon={<EyeOutlined />} size="small" onClick={() => viewDetails(record)} />
                    </Tooltip>
                    <Tooltip title="Edit Header">
                        <Button icon={<EditOutlined />} size="small" onClick={() => openEditModal(record)} />
                    </Tooltip>
                    <Tooltip title="Edit Items">
                        <Button icon={<PlusOutlined />} size="small" onClick={() => openItemsModal(record)} />
                    </Tooltip>
                    <Tooltip title="Record Payment">
                        <Button
                            icon={<DollarOutlined />}
                            size="small"
                            onClick={() => openPaymentModal(record)}
                            disabled={record.totalCost - (record.paidAmount || 0) <= 0}
                        />
                    </Tooltip>
                    {!record.reversed && (
                        <Tooltip title="Reverse Purchase">
                            <Popconfirm
                                title="Reverse Purchase"
                                description={`Are you sure you want to reverse purchase ${record.transactionId}? This will remove all items from inventory.`}
                                onConfirm={() => handleReversePurchase(record)}
                                okText="Yes, Reverse"
                                cancelText="Cancel"
                                okButtonProps={{ danger: true }}
                            >
                                <Button icon={<RollbackOutlined />} size="small" danger />
                            </Popconfirm>
                        </Tooltip>
                    )}
                </Space>
            )
        }
    ];

    // Columns for Consumptions tab
    const consumptionColumns = [
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            valueType: 'date',
            sorter: true,
            render: (_, record) => moment(record.date).format('DD/MM/YYYY')
        },
        {
            title: 'Item',
            dataIndex: 'item_name',
            key: 'item_name'
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity'
        },
        {
            title: 'Unit',
            dataIndex: 'unit',
            key: 'unit'
        },
        {
            title: 'Unit Cost (KES)',
            dataIndex: 'unit_cost',
            key: 'unit_cost',
            render: (val) => val?.toFixed(2)
        },
        {
            title: 'Purpose',
            dataIndex: 'purpose',
            key: 'purpose',
            ellipsis: true
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 170,
            render: (_, record) => (
                <Space>
                    <Button icon={<EyeOutlined />} size="small" onClick={() => viewDetails(record)} />

                    <Popconfirm
                        title="Reverse this consumption? Stock will be added back."
                        onConfirm={() => handleReverseConsumption(record.id)}
                    >
                        <Button icon={<RollbackOutlined />} danger size="small">
                            Reverse
                        </Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    // Columns for Inventory Summary (Current Stock with full history)
    const inventorySummaryColumns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            sorter: true
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            filters: true,
            valueEnum: {
                feed: { text: 'Feed' },
                vaccine: { text: 'Vaccine' },
                water: { text: 'Water' },
                supplement: { text: 'Supplement' },
                general: { text: 'General' },
                food: { text: 'Food' },
                other: { text: 'Other' }
            }
        },
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
            render: (text) => <span className="text-capitalize">{text}</span>
        },
        {
            title: 'Unit',
            dataIndex: 'unit',
            key: 'unit',
            render: (text) => <span className="text-capitalize">{text}</span>
        },
        {
            title: 'Initial Stock',
            dataIndex: 'initial_stock',
            key: 'initial_stock',
            sorter: true
        },
        {
            title: 'Restocked',
            dataIndex: 'total_restocked',
            key: 'total_restocked',
            sorter: true
        },
        {
            title: 'Restock Count',
            dataIndex: 'restock_count',
            key: 'restock_count',
            sorter: true
        },
        {
            title: 'Total Consumed',
            dataIndex: 'total_consumed',
            key: 'total_consumed',
            sorter: true
        },
        {
            title: 'Consumption Count',
            dataIndex: 'consumption_count',
            key: 'consumption_count',
            sorter: true
        },
        {
            title: 'Current Stock',
            dataIndex: 'current_stock',
            key: 'current_stock',
            sorter: true,
            render: (stock, record) => <Badge status={stock <= 10 ? 'error' : 'success'} text={`${stock} ${record.unit}`} />
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            render: (_, record) => (
                <Space>
                    <Link to={`/inventory/item/${record.id}`}>
                        <Button icon={<EyeOutlined />} size="small">
                            Details
                        </Button>
                    </Link>
                    <Tooltip title="Edit">
                        <Button icon={<EditOutlined />} size="small" onClick={() => openItemEditModal(record)} />
                    </Tooltip>
                </Space>
            )
        }
    ];

    const summary = useMemo(() => {
        const totalQuantity = animalReportData.reduce((sum, r) => sum + r.quantity, 0);
        const totalCost = animalReportData.reduce((sum, r) => sum + (r.total_cost || 0), 0);
        return { totalQuantity, totalCost };
    }, [animalReportData]);

    useEffect(() => {
        if (!selectedRecord) return;

        const fetchIdentifiers = async () => {
            setLoadingIdentifiers(true);

            const data = await loadIdentifiersForRecord(selectedRecord);
            // const data = await getAnimalIdentifiers(selectedRecord);

            setIdentifiers(data);
            setLoadingIdentifiers(false);
        };

        fetchIdentifiers();
    }, [selectedRecord]);

    // ---- Edit restock (more fields) ----
    const openEditModal = (record) => {
        setSelectedRecord(record);
        editForm.setFieldsValue({
            supplier: record.stakeholderId,
            totalCost: record.totalCost,
            paidAmount: record.paidAmount,
            paymentMethod: record.paymentMethod,
            paymentStatus: record.paymentStatus,
            dueDate: record.dueDate ? moment(record.dueDate) : null,
            recordedDate: record.date ? moment(record.date) : null,
            notes: record.notes,
            remarks: record.remarks
        });
        setEditModalVisible(true);
    };

    // Save header changes
    const handleEditSave = async (values) => {
        try {
            const oldPaidAmount = selectedRecord.paidAmount || 0;
            const newPaidAmount = values.paidAmount || 0;

            await api.put(`/api/inventory-headers/${selectedRecord.id}`, {
                stakeholderId: values.supplier,
                totalCost: values.totalCost,
                paidAmount: newPaidAmount,
                oldPaidAmount: oldPaidAmount,
                amountPaidTotal: selectedRecord.amountPaidTotal || 0,
                paymentMethod: values.paymentMethod,
                paymentStatus: values.paymentStatus,
                dueDate: values.dueDate ? values.dueDate.format('YYYY-MM-DD') : null,
                recorded: values.recordedDate ? values.recordedDate.format('YYYY-MM-DD') : null,
                notes: values.notes,
                remarks: values.remarks,
                farm: values.farm,
                inventoryType: values.inventoryType,
                userId: 1,
                userName: 'Admin'
            });

            message.success('Inventory updated successfully');
            setEditModalVisible(false);
            actionRef.current?.reload();
            loadStats();
        } catch (error) {
            console.error('Error updating inventory:', error);
            message.error('Update failed');
        }
    };

    // ---- Payment handling (with reference, user selection, balance check) ----
    const openPaymentModal = (record) => {
        setSelectedRecord(record);
        paymentForm.resetFields();
        paymentForm.setFieldsValue({
            paymentDate: moment(),
            paymentMethod: 'cash',
            paidBy: account?.id
        });
        setPaymentModalVisible(true);
    };

    const handlePayment = async (values) => {
        const totalCost = Number(selectedRecord?.totalCost ?? 0);
        const paidAmount = Number(selectedRecord?.paidAmount ?? 0);
        const paymentAmount = Number(values?.amount ?? 0);

        // Round everything to 2 decimal places
        const remaining = Number((totalCost - paidAmount).toFixed(2));
        const amount = Number(paymentAmount.toFixed(2));

        console.log('Payment calculation:', {
            totalCost,
            paidAmount,
            amount,
            remaining,
            comparison: amount > remaining
        });

        if (amount <= 0) {
            message.error('Please enter a valid payment amount.');
            return;
        }

        if (amount > remaining + 0.01) {
            message.error(`Payment exceeds remaining balance of KES ${remaining.toFixed(2)}, Already paid: KES ${paidAmount.toFixed(2)}`);
            return;
        }

        try {
            const response = await api.post(`/record-purchase-payment`, {
                transactionId: selectedRecord.id,
                amount: amount,
                paymentMethod: values.paymentMethod,
                paymentDate: values.paymentDate.format('YYYY-MM-DD'),
                paidBy: values.paidBy || account?.id,
                userName: account.firstname,
                userId: account.id,
                note: values.note,
                creditAccount: values.creditAccount,
                pay_reference: values.pay_reference,
                gl_debit: selectedRecord.gl_debit,
                gl_credit: selectedRecord.gl_credit,
                tableName: 'inventory_headers',
                record_id: selectedRecord.id,
                stakeholderId: selectedRecord.stakeholderId
            });

            if (response.data.success) {
                message.success('Payment recorded');
                setPaymentModalVisible(false);
                actionRef.current?.reload();
                loadStats(statsFilters);
                fetchRestocks();
            } else {
                message.error(response.data.message || 'Payment failed');
            }
        } catch (error) {
            console.error('Payment error:', error);
            message.error(error?.response?.data?.message || 'Payment failed');
        }
    };

    // const handlePayment = async (values) => {
    //     const remaining = selectedRecord.totalCost - (selectedRecord.paidAmount || 0);
    //     if (values.amount > remaining) {
    //         message.error(
    //             `Payment exceeds remaining balance of KES ${remaining.toFixed(2)}, Already paid: KES ${selectedRecord.paidAmount || 0}`
    //         );
    //         return;
    //     }
    //     console.log("Payment values:", values);
    //     try {
    //         const response = await api.post(`/record-purchase-payment`, {
    //             transactionId: selectedRecord.id,
    //             amount: values.amount,
    //             paymentMethod: values.paymentMethod,
    //             paymentDate: values.paymentDate.format('YYYY-MM-DD'),
    //             paidBy: values.paidBy || account?.id,
    //             userName: account.firstname,
    //             userId: account.id,
    //             note: values.note,
    //             creditAccount: values.creditAccount,
    //             pay_reference: values.pay_reference,
    //             gl_debit: selectedRecord.gl_debit,
    //             gl_credit: selectedRecord.gl_credit,
    //             tableName: 'inventory_headers',
    //             record_id: selectedRecord.id,
    //             stakeholderId: selectedRecord.stakeholderId
    //         });
    //         if (response.data.success) {
    //             message.success('Payment recorded');
    //             setPaymentModalVisible(false);
    //             actionRef.current?.reload();
    //             loadStats(statsFilters);
    //             fetchRestocks(); // Refresh to get updated payment status
    //         } else {
    //             message.error(response.data.message || 'Payment failed');
    //         }
    //     } catch (error) {
    //         message.error('Payment failed');
    //     }
    // };

    // Consumption reversal
    const handleReverseConsumption = async (id) => {
        try {
            await reverseConsumption(id);
            message.success('Consumption reversed');
            actionRef.current?.reload();
        } catch (error) {
            message.error('Reversal failed');
        }
    };

    // ---- Multi-reversal for consumptions ----
    const handleBatchReverse = async () => {
        if (selectedRows.length === 0) return;
        Modal.confirm({
            title: `Reverse ${selectedRows.length} consumption(s)?`,
            content: 'Stock will be added back for each selected record.',
            onOk: async () => {
                try {
                    const promises = selectedRows.map((id) => reverseConsumption(id));
                    await Promise.all(promises);
                    message.success(`${selectedRows.length} consumption(s) reversed`);
                    setSelectedRows([]);
                    actionRef.current?.reload();
                } catch (error) {
                    message.error('Batch reversal failed');
                }
            }
        });
    };

    // Item editing
    const openItemEditModal = (record) => {
        setCurrentItem(record);
        itemEditForm.setFieldsValue({
            name: record.name,
            unit: record.unit,
            type: record.type,
            current_stock: record.current_stock,
            category: record.category
        });
        setItemEditVisible(true);
    };

    const handleItemUpdate = async (values) => {
        try {
            await updateItem(currentItem.id, values);
            message.success('Item updated');
            setItemEditVisible(false);
            actionRef.current?.reload();
            fetchInventorySummary();
        } catch (error) {
            message.error('Update failed');
        }
    };

    const handleDownloadReceipt = async (record) => {
        await exportReceipts({ id: record.id, format: 'pdf' });
    };

    // Save items changes
    const handleItemsSave = async (values) => {
        try {
            const itemsToSave = values.items.map((item) => ({
                item_id: item.item_id,
                quantity: item.quantity,
                unit_cost: item.unit_cost,
                total_cost: item.quantity * item.unit_cost
            }));

            const response = await api.put(`/inventory-items/${selectedRecord.id}`, {
                items: itemsToSave,
                userId: 1,
                userName: 'Admin'
            });

            message.success(`Items updated successfully. New total: ${formatKES(response.data.newTotal)}`);
            setItemsModalVisible(false);
            actionRef.current?.reload();
            loadStats();
        } catch (error) {
            console.error('Error updating items:', error);
            message.error('Failed to update items');
        }
    };

    const viewImage = (file) => {
        if (file) window.open(`/uploads/${file}`, '_blank');
    };

    //     console.log("selected record",selectedRecord)

    useEffect(() => {
        if (selectedRecord) {
            setBalance((selectedRecord.totalCost - (selectedRecord.paidAmount || 0)).toFixed(2));
        }
    }, [selectedRecord, paymentForm]);

    return (
        <>
            {/* Main Tabs */}
            <Card
                title="Inventory Management"
                extra={
                    <Row gutter={16}>
                        <Col>
                            <Link to="/inventory/add">
                                <Button type="primary" icon={<ShoppingOutlined />}>
                                    Restock
                                </Button>
                            </Link>
                        </Col>
                        <Col>
                            <Link to="/inventory/consumption">
                                <Button icon={<MedicineBoxOutlined />}>Log Consumption</Button>
                            </Link>
                        </Col>
                    </Row>
                }
            >
                <Tabs activeKey={activeTab} onChange={setActiveTab}>
                    <TabPane
                        tab={
                            <span>
                                <ShoppingOutlined /> Restocks
                            </span>
                        }
                        key="restocks"
                    >
                        {/* Stats Cards */}
                        {stats && (
                            <>
                                <Row gutter={16} style={{ marginBottom: 24 }}>
                                    <Col span={6}>
                                        {/* <Card> */}
                                        <Statistic title="Total Purchases" value={stats.total_purchases} precision={2} prefix="KES" />
                                        {/* </Card> */}
                                    </Col>
                                    <Col span={6}>
                                        {/* <Card> */}
                                        <Statistic title="Total Paid" value={stats.total_paid} precision={2} prefix="KES" />
                                        {/* </Card> */}
                                    </Col>
                                    <Col span={6}>
                                        {/* <Card> */}
                                        <Statistic
                                            title="Balance"
                                            value={stats.total_balance}
                                            precision={2}
                                            prefix="KES"
                                            valueStyle={{ color: stats.total_balance > 0 ? '#cf1322' : '#3f8600' }}
                                        />
                                        {/* </Card> */}
                                    </Col>
                                    <Col span={6}>
                                        {/* <Card> */}
                                        <Statistic
                                            title="Overdue"
                                            value={stats.overdue_count}
                                            suffix="invoices"
                                            valueStyle={{ color: '#cf1322' }}
                                        />
                                        {/* </Card> */}
                                    </Col>
                                </Row>
                            </>
                        )}

                        <Divider />

                        <ProTable
                            actionRef={actionRef}
                            columns={restockColumns}
                            request={fetchRestocks}
                            rowKey="id"
                            search={{
                                labelWidth: 'auto',
                                defaultCollapsed: true,
                                optionRender: ({ searchText, resetText }, { form }) => [
                                    <Button key="reset" onClick={() => form.resetFields()}>
                                        {resetText}
                                    </Button>,
                                    <Button key="search" type="primary" onClick={() => form.submit()}>
                                        {searchText}
                                    </Button>
                                ]
                            }}
                            pagination={{
                                showSizeChanger: true,
                                showQuickJumper: true
                            }}
                            scroll={{ x: 1200 }}
                            headerTitle="Restock Transactions"
                            toolBarRender={() => [
                                <Button key="export" icon={<InboxOutlined />} onClick={exportRestocksData}>
                                    Export CSV
                                </Button>
                            ]}
                            // onRow={(record) => {
                            //     return {
                            //         onClick: () => {
                            //             window.location.href = `/inventory/item/${record.id}`;
                            //         }
                            //     };
                            // }}
                            defaultSize="small"
                        />
                    </TabPane>

                    <TabPane
                        tab={
                            <span>
                                <MedicineBoxOutlined /> Consumption
                            </span>
                        }
                        key="consumptions"
                    >
                        {/* Stats Cards */}
                        <Row gutter={16} style={{ marginBottom: 16 }}>
                            <Col span={8}>
                                {/* <Card> */}
                                <Statistic title="Total Records" value={consumptionStats.record_count} />
                                {/* </Card> */}
                            </Col>
                            <Col span={8}>
                                {/* <Card> */}
                                <Statistic title="Total Quantity" value={consumptionStats.total_quantity} precision={2} />
                                {/* </Card> */}
                            </Col>
                            <Col span={8}>
                                {/* <Card> */}
                                <Statistic title="Total Cost (KES)" value={consumptionStats.total_cost} precision={2} prefix="KES" />
                                {/* </Card> */}
                            </Col>
                        </Row>
                        <Divider />
                        <ProTable
                            actionRef={consumptionActionRef}
                            columns={consumptionColumns}
                            request={fetchConsumptionsWithFilters}
                            rowKey="id"
                            search={false}
                            scroll={{ x: 1200 }}
                            // headerTitle="Consumption Records"
                            rowSelection={{ selectedRowKeys: selectedRows, onChange: setSelectedRows }}
                            toolBarRender={() => [
                                <Space key="toolbar">
                                    <RangePicker
                                        style={{ width: '100%' }}
                                        onChange={(dates) => setConsumptionFilters((prev) => ({ ...prev, dateRange: dates }))}
                                    />

                                    <Select
                                        placeholder="Select type"
                                        allowClear
                                        onChange={(val) => setConsumptionFilters((prev) => ({ ...prev, type: val, item_id: null }))}
                                    >
                                        {consumptionTypes.map((t) => (
                                            <Option key={t} value={t} className="text-capitalize">
                                                {t}
                                            </Option>
                                        ))}
                                    </Select>

                                    <Select
                                        placeholder="Select category"
                                        allowClear
                                        onChange={(val) => setConsumptionFilters((prev) => ({ ...prev, category: val, item_id: null }))}
                                    >
                                        {consumptionCategories.map((c) => (
                                            <Option key={c} value={c} className="text-capitalize">
                                                {c}
                                            </Option>
                                        ))}
                                    </Select>

                                    <Select
                                        placeholder="Select item"
                                        allowClear
                                        showSearch
                                        optionFilterProp="children"
                                        onChange={(val) => setConsumptionFilters((prev) => ({ ...prev, item_id: val }))}
                                    >
                                        {consumptionItems
                                            .filter(
                                                (i) =>
                                                    (!consumptionFilters.type || i.type === consumptionFilters.type) &&
                                                    (!consumptionFilters.category || i.category === consumptionFilters.category)
                                            )
                                            .map((item) => (
                                                <Option key={item.id} value={item.id} className="text-capitalize">
                                                    {item.name}
                                                </Option>
                                            ))}
                                    </Select>

                                    <Select
                                        placeholder="All animals"
                                        allowClear
                                        showSearch
                                        optionFilterProp="children"
                                        onChange={(val) => setConsumptionFilters((prev) => ({ ...prev, animal_id: val }))}
                                    >
                                        {animalsList?.map((animal) => (
                                            <Option key={animal.id} value={animal.id}>
                                                {animal.identifier} ({animal.type_slug})
                                            </Option>
                                        ))}
                                    </Select>

                                    <Button type="primary" onClick={() => consumptionActionRef.current?.reload()}>
                                        Search
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            setConsumptionFilters({
                                                dateRange: null,
                                                type: null,
                                                category: null,
                                                item_id: null,
                                                animal_id: null
                                            });
                                            consumptionActionRef.current?.reload();
                                        }}
                                    >
                                        Reset
                                    </Button>
                                    <Button key="export" icon={<InboxOutlined />} onClick={exportConsumptionsData}>
                                        CSV
                                    </Button>
                                    <Popconfirm
                                        title="Reverse selected consumptions?"
                                        onConfirm={handleBatchReverse}
                                        disabled={selectedRows.length === 0}
                                    >
                                        <Button danger icon={<RollbackOutlined />} disabled={selectedRows.length === 0}>
                                            Reverse
                                        </Button>
                                    </Popconfirm>
                                </Space>
                            ]}
                            defaultSize="small"
                        />
                    </TabPane>

                    <TabPane
                        tab={
                            <span>
                                <BarChartOutlined /> Current Stock
                            </span>
                        }
                        key="current"
                    >
                        {/* <Alert
                            message="Low Stock Alert"
                            description="Items with current stock below 10 units are highlighted."
                            type="warning"
                            showIcon
                            style={{ marginBottom: 16 }}
                            time={400}
                        /> */}

                        {stats && (
                            <>
                                <Row gutter={16} style={{ marginBottom: 24 }}>
                                    <Col xs={24} sm={4}>
                                        {/* <Card> */}
                                        <Statistic
                                            title="Total Inventory Value"
                                            value={stats.totalValue || 0}
                                            precision={2}
                                            prefix="KES"
                                            valueStyle={{ color: '#3f8600' }}
                                        />
                                        {/* </Card> */}
                                    </Col>
                                    {stats.byType?.map((typeStat) => (
                                        <Col xs={24} sm={5} key={typeStat.type}>
                                            {/* <Card> */}
                                            <Statistic
                                                title={typeStat.type.toUpperCase()}
                                                value={typeStat.value || 0}
                                                precision={2}
                                                prefix="KES"
                                            />
                                            {/* </Card> */}
                                        </Col>
                                    ))}
                                </Row>
                            </>
                        )}

                        <Divider />

                        <ProTable
                            columns={inventorySummaryColumns}
                            request={fetchInventorySummary}
                            rowKey="id"
                            search={{ labelWidth: 'auto', defaultCollapsed: true }}
                            pagination={{ showSizeChanger: true, showQuickJumper: true }}
                            scroll={{ x: 1400 }}
                            headerTitle="Inventory Summary (Initial + Restocks - Consumptions)"
                            toolBarRender={() => [
                                <Button key="export" icon={<DownloadOutlined />} onClick={exportStockData}>
                                    Export Excel
                                </Button>
                            ]}
                            defaultSize="small"
                        />
                    </TabPane>
                </Tabs>
            </Card>

            {/* Edit Header Modal */}
            <Modal
                title="Edit Inventory Header"
                open={editModalVisible}
                onCancel={() => setEditModalVisible(false)}
                footer={null}
                width={600}
            >
                <Form form={editForm} onFinish={handleEditSave} layout="vertical">
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="supplier" label="Supplier">
                                <Select showSearch optionFilterProp="children">
                                    {suppliers.map((s) => (
                                        <Option key={s.id} value={s.id}>
                                            {s.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="recordedDate" label="Recorded Date">
                                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="inventoryType" label="Inventory Type">
                                <Select>
                                    <Option value="new_stock">New Stock</Option>
                                    <Option value="restock">Restock</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="farm" label="Farm">
                                <Input placeholder="Farm name" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="totalCost" label="Total Cost (KES)">
                                <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="paidAmount" label="Paid Amount (KES)">
                                <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="paymentMethod" label="Payment Method">
                                <Select>
                                    <Option value="cash">Cash</Option>
                                    <Option value="mpesa">M-Pesa</Option>
                                    <Option value="bank_transfer">Bank Transfer</Option>
                                    <Option value="cheque">Cheque</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="paymentStatus" label="Payment Status">
                                <Select>
                                    <Option value="unpaid">Unpaid</Option>
                                    <Option value="partially_paid">Partial</Option>
                                    <Option value="fully_paid">Paid</Option>
                                    <Option value="overpaid">Overpaid</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="dueDate" label="Due Date">
                                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="notes" label="Notes">
                        <Input.TextArea rows={2} />
                    </Form.Item>

                    <Form.Item name="remarks" label="Remarks">
                        <Input.TextArea rows={2} />
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit">
                            Save Changes
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Edit Items Modal */}
            <Modal
                title={`Edit Items - ${selectedRecord?.transactionId || selectedRecord?.id}`}
                open={itemsModalVisible}
                onCancel={() => setItemsModalVisible(false)}
                width={800}
                footer={null}
            >
                <Form form={itemsForm} onFinish={handleItemsSave}>
                    <Form.List name="items">
                        {(fields, { add, remove }) => (
                            <>
                                <Table
                                    dataSource={fields}
                                    columns={[
                                        {
                                            title: 'Item',
                                            dataIndex: 'item_id',
                                            key: 'item_id',
                                            render: (_, field, index) => (
                                                <Form.Item
                                                    name={[field.name, 'item_id']}
                                                    noStyle
                                                    rules={[{ required: true, message: 'Select item' }]}
                                                >
                                                    <Select
                                                        showSearch
                                                        placeholder="Select item"
                                                        optionFilterProp="children"
                                                        style={{ width: 200 }}
                                                        onChange={(value) => {
                                                            const selected = allItems.find((i) => i.id === value);
                                                            if (selected) {
                                                                const items = itemsForm.getFieldValue('items') || [];
                                                                items[field.name].item_name = selected.name;
                                                                items[field.name].unit = selected.unit;
                                                                itemsForm.setFieldsValue({ items });
                                                            }
                                                        }}
                                                    >
                                                        {allItems.map((item) => (
                                                            <Option key={item.id} value={item.id}>
                                                                {item.name} ({item.unit})
                                                            </Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                            )
                                        },
                                        {
                                            title: 'Item Name',
                                            dataIndex: 'item_name',
                                            key: 'item_name',
                                            render: (_, field) => (
                                                <Form.Item name={[field.name, 'item_name']} noStyle>
                                                    <Input placeholder="Item name" />
                                                </Form.Item>
                                            )
                                        },
                                        {
                                            title: 'Quantity',
                                            dataIndex: 'quantity',
                                            key: 'quantity',
                                            render: (_, field) => (
                                                <Form.Item
                                                    name={[field.name, 'quantity']}
                                                    noStyle
                                                    rules={[{ required: true, message: 'Required' }]}
                                                >
                                                    <InputNumber min={0.01} step={0.01} style={{ width: 100 }} />
                                                </Form.Item>
                                            )
                                        },
                                        {
                                            title: 'Unit Cost (KES)',
                                            dataIndex: 'unit_cost',
                                            key: 'unit_cost',
                                            render: (_, field) => (
                                                <Form.Item
                                                    name={[field.name, 'unit_cost']}
                                                    noStyle
                                                    rules={[{ required: true, message: 'Required' }]}
                                                >
                                                    <InputNumber min={0} step={0.01} style={{ width: 120 }} />
                                                </Form.Item>
                                            )
                                        },
                                        {
                                            title: 'Total',
                                            key: 'total',
                                            render: (_, field) => {
                                                const quantity = itemsForm.getFieldValue(['items', field.name, 'quantity']) || 0;
                                                const unitCost = itemsForm.getFieldValue(['items', field.name, 'unit_cost']) || 0;
                                                const total = quantity * unitCost;
                                                return <span>{formatKES(total)}</span>;
                                            }
                                        },
                                        {
                                            title: '',
                                            key: 'action',
                                            render: (_, field) => (
                                                <Button danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} size="small" />
                                            )
                                        }
                                    ]}
                                    rowKey={(field) => field.key}
                                    footer={() => (
                                        <Button type="dashed" onClick={() => add({})} block icon={<PlusOutlined />}>
                                            Add Item
                                        </Button>
                                    )}
                                />

                                <div style={{ textAlign: 'right', marginTop: 16, padding: 12, background: '#f5f5f5', borderRadius: 8 }}>
                                    <strong>Grand Total: </strong>
                                    {formatKES(
                                        (itemsForm.getFieldValue('items') || []).reduce(
                                            (sum, item) => sum + (item.quantity || 0) * (item.unit_cost || 0),
                                            0
                                        )
                                    )}
                                </div>
                            </>
                        )}
                    </Form.List>

                    <Form.Item style={{ marginTop: 16 }}>
                        <Button type="primary" htmlType="submit">
                            Save All Items
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Payment Modal (with reference, paidBy selection) */}
            {/* <Modal title="Record Payment" open={paymentModalVisible} onCancel={() => setPaymentModalVisible(false)} footer={null}>
                <Form form={paymentForm} onFinish={handlePayment} layout="vertical">
                    <Form.Item name="amount" label="Amount" rules={[{ required: true }]}>
                        <InputNumber min={0.01} step={0.01} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="paymentMethod" label="Method" rules={[{ required: true }]}>
                        <Select>
                            <Option value="cash">Cash</Option>
                            <Option value="mpesa">M-Pesa</Option>
                            <Option value="bank_transfer">Bank Transfer</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="paymentDate" label="Date" rules={[{ required: true }]}>
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="paidBy" label="Paid By" rules={[{ required: true }]}>
                        <Select>
                            {users.map((u) => (
                                <Option key={u.id} value={u.id}>
                                    {u.firstname} {u.lastname}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="pay_reference" label="Reference (M-Pesa code, Cheque no.)">
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="creditAccount"
                        label="Credit Account"
                        rules={[
                            {
                                required: true,
                                message: 'Please select credit account'
                            }
                        ]}
                    >
                        <Select options={paidOptions} placeholder="Select credit account" showSearch optionFilterProp="label" />
                    </Form.Item>
                    <Form.Item name="note" label="Note">
                        <Input.TextArea rows={2} />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit">
                            Record Payment
                        </Button>
                    </Form.Item>
                </Form>
            </Modal> */}

            <Modal title="Record Payment" open={paymentModalVisible} onCancel={() => setPaymentModalVisible(false)} footer={null}>
                <Form form={paymentForm} onFinish={handlePayment} layout="vertical">
                    <Alert
                        banner
                        type="info"
                        message={`${selectedRecord?.transactionId || ''}, Balance: KES ${balance}`}
                        style={{ marginBottom: 16 }}
                    />
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name="amount" label="Amount" rules={[{ required: true }]}>
                                <InputNumber min={0.01} step={0.01} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item name="paymentMethod" label="Method" rules={[{ required: true }]}>
                                <Select>
                                    <Option value="cash">Cash</Option>
                                    <Option value="mpesa">M-Pesa</Option>
                                    <Option value="bank_transfer">Bank Transfer</Option>
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item name="paymentDate" label="Date" rules={[{ required: true }]}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item name="paidBy" label="Paid By" rules={[{ required: true }]}>
                                <Select>
                                    {users.map((u) => (
                                        <Option key={u.id} value={u.id}>
                                            {u.firstname} {u.lastname}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item name="pay_reference" label="Reference">
                                <Input placeholder="Reference (M-Pesa code, Cheque no.)" />
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item
                                name="creditAccount"
                                label="Credit Account"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please select credit account'
                                    }
                                ]}
                            >
                                <Select options={paidOptions} placeholder="Select credit account" showSearch optionFilterProp="label" />
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item name="note" label="Note">
                                <Input.TextArea rows={2} />
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item style={{ marginBottom: 0 }}>
                                <Button type="primary" htmlType="submit">
                                    Record Payment
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>

            {/* Edit Item Modal (with type and unit selection) */}
            <Modal title="Edit Item" open={itemEditVisible} onCancel={() => setItemEditVisible(false)} footer={null}>
                <Form form={itemEditForm} onFinish={handleItemUpdate} layout="vertical">
                    <Form.Item name="name" label="Name" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="type" label="Type" rules={[{ required: true }]}>
                        <Select>
                            <Option value="feed">Feed</Option>
                            <Option value="vaccine">Vaccine</Option>
                            <Option value="water">Water</Option>
                            <Option value="supplement">Supplement</Option>
                            <Option value="food">Food</Option>
                            <Option value="other">Other</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="category" label="Category">
                        <Select>
                            <Option value="pig">Pig</Option>
                            <Option value="goat">Goat</Option>
                            <Option value="sheep">Sheep</Option>
                            <Option value="weaner">Weaner</Option>
                            <Option value="poultry">Poultry</Option>
                            <Option value="general">General</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="unit" label="Unit" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="current_stock" label="Current Stock">
                        <Input disabled />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit">
                            Update Item
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

            {/* // Reversal Modal Component */}
            <Modal
                title={
                    <Space>
                        <RollbackOutlined style={{ color: '#ff4d4f' }} />
                        <span>Reverse Purchase</span>
                    </Space>
                }
                open={reversalModalVisible}
                onCancel={() => {
                    setReversalModalVisible(false);
                    setSelectedPurchase(null);
                    reversalForm.resetFields();
                }}
                onOk={confirmReversePurchase}
                confirmLoading={reversing}
                okButtonProps={{ danger: true }}
                width={500}
            >
                <Alert
                    message="Warning: This action cannot be undone"
                    description="Reversing will remove all items from inventory and reverse accounting entries."
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                />

                {selectedPurchase && (
                    <Descriptions size="small" bordered column={1} style={{ marginBottom: 16 }}>
                        <Descriptions.Item label="Transaction ID">{selectedPurchase.transactionId}</Descriptions.Item>
                        <Descriptions.Item label="Supplier">{supplierMap[selectedPurchase.stakeholderId] || '—'}</Descriptions.Item>
                        <Descriptions.Item label="Total Cost">{formatKES(selectedPurchase.totalCost)}</Descriptions.Item>
                        <Descriptions.Item label="Items">{selectedPurchase?.items?.length || 0} items</Descriptions.Item>
                        <Descriptions.Item label="Date">{formatFullDate(selectedPurchase.recorded)}</Descriptions.Item>
                    </Descriptions>
                )}

                <Form form={reversalForm} layout="vertical">
                    <Form.Item name="reason" label="Reversal Reason" rules={[{ required: true, message: 'Please select a reason' }]}>
                        <Select placeholder="Select a reason">
                            <Option value="wrong_entry">Wrong entry / Data entry error</Option>
                            <Option value="duplicate">Duplicate entry</Option>
                            <Option value="return_to_supplier">Return to supplier</Option>
                            <Option value="quality_issue">Quality issue / Damaged goods</Option>
                            <Option value="price_correction">Price correction</Option>
                            <Option value="other">Other</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item name="reason_detail" label="Additional Details">
                        <TextArea rows={3} placeholder="Provide more details..." />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Details Drawer */}
            <Drawer
                title={
                    <Space>
                        <EyeOutlined />
                        <span>{activeTab === 'restocks' ? 'Restock Details' : 'Consumption Details'}</span>
                    </Space>
                }
                width={600}
                onClose={closeDetails}
                open={detailsVisible}
                footer={null}
                closeIcon={<CloseOutlined />}
            >
                {selectedRecord && (
                    <>
                        {activeTab === 'restocks' ? (
                            // Restock details
                            <>
                                <Descriptions title="Header Information" bordered column={1} size="small">
                                    <Descriptions.Item label="Transaction ID">{selectedRecord.transactionId}</Descriptions.Item>
                                    <Descriptions.Item label="Invoice Number">
                                        {selectedRecord.invoice_number || selectedRecord.remarks || '-'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Date">{moment(selectedRecord.date).format('DD/MM/YYYY')}</Descriptions.Item>
                                    <Descriptions.Item label="Supplier">
                                        {selectedRecord.stakeholderId ? supplierMap[selectedRecord.stakeholderId] : '-'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Total Cost">KES {selectedRecord.totalCost?.toFixed(2)}</Descriptions.Item>
                                    <Descriptions.Item label="Paid Amount">KES {selectedRecord.paidAmount?.toFixed(2)}</Descriptions.Item>
                                    <Descriptions.Item label="Payment Status">
                                        <Tag
                                            color={
                                                selectedRecord.paymentStatus === 'paid'
                                                    ? 'green'
                                                    : selectedRecord.paymentStatus === 'partial'
                                                    ? 'orange'
                                                    : 'red'
                                            }
                                        >
                                            {selectedRecord.paymentStatus?.toUpperCase()}
                                        </Tag>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Payment Method">{selectedRecord.paymentMethod || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="Delivery Method">{selectedRecord.delivery || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="Delivery Person">{selectedRecord.delGuy || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="Farm">{selectedRecord.farm || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="Debit Account">{selectedRecord.gl_debit || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="Credit Account">{selectedRecord.gl_credit || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="Served By">
                                        {selectedRecord.servedBy ? userMap[selectedRecord.servedBy] : '-'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Notes">{selectedRecord.notes || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="Remarks">{selectedRecord.remarks || '-'}</Descriptions.Item>
                                </Descriptions>
                                <Divider />
                                <Title level={5}>Items Received</Title>
                                <Table
                                    dataSource={selectedRecord.items || []}
                                    columns={[
                                        { title: 'Item', dataIndex: 'item_name', key: 'item_name' },
                                        { title: 'Qty', dataIndex: 'quantity', key: 'quantity' },
                                        {
                                            title: 'Unit Cost',
                                            dataIndex: 'unit_cost',
                                            key: 'unit_cost',
                                            render: (val) => formatKES(val)
                                        },
                                        {
                                            title: 'Total',
                                            dataIndex: 'total_cost',
                                            key: 'total_cost',
                                            render: (val) => formatKES(val)
                                        }
                                    ]}
                                    pagination={false}
                                    size="small"
                                />
                                <Divider />
                                <Title level={5}>Payments Received</Title>
                                <Table
                                    dataSource={receipts}
                                    loading={loadingReceipts}
                                    rowKey="id"
                                    columns={[
                                        { title: 'Payment Method', dataIndex: 'paymentMethod', key: 'paymentMethod' },
                                        {
                                            title: 'Amount',
                                            dataIndex: 'amount',
                                            key: 'amount',
                                            render: (val) => formatKES(val)
                                        },
                                        {
                                            title: 'Paid By',
                                            dataIndex: 'paidBy',
                                            key: 'paidBy'
                                        },
                                        {
                                            title: 'Date',
                                            dataIndex: 'recorded',
                                            key: 'recorded',
                                            render: (val) => moment(val).format('DD/MM/YYYY')
                                        },
                                        {
                                            title: 'Action',
                                            key: 'action',
                                            render: (_, record) => (
                                                <Button
                                                    icon={<DownloadOutlined />}
                                                    size="small"
                                                    onClick={() => handleDownloadReceipt(record)}
                                                >
                                                    Receipt
                                                </Button>
                                            )
                                        }
                                    ]}
                                    pagination={false}
                                    size="small"
                                />

                                <Card title="Attached Files" style={{ marginTop: 10 }}>
                                    {!selectedRecord.image && <Text type="secondary">No attached files</Text>}
                                    {selectedRecord.image &&
                                        (() => {
                                            let files = [];

                                            try {
                                                files =
                                                    typeof selectedRecord.image === 'string'
                                                        ? JSON.parse(selectedRecord.image)
                                                        : selectedRecord.image;

                                                if (!Array.isArray(files)) {
                                                    files = [files];
                                                }
                                            } catch (e) {
                                                console.error('Invalid attachment JSON:', e);
                                                return <span>Invalid attachment</span>;
                                            }

                                            return (
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                                                    {files.map((file, index) => {
                                                        const fileName = typeof file === 'string' ? file : file.filename;

                                                        const originalName =
                                                            typeof file === 'object' && file.originalName ? file.originalName : fileName;

                                                        const fileUrl = `/uploads/${fileName}`;

                                                        const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);

                                                        return (
                                                            <div key={index}>
                                                                {isImage ? (
                                                                    <img
                                                                        src={fileUrl}
                                                                        alt={fileName}
                                                                        style={{
                                                                            width: 120,
                                                                            height: 120,
                                                                            objectFit: 'cover',
                                                                            borderRadius: 8,
                                                                            cursor: 'pointer',
                                                                            border: '1px solid #ddd'
                                                                        }}
                                                                        onClick={() => viewImage(file)}
                                                                    />
                                                                ) : (
                                                                    <a
                                                                        href={fileUrl}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        style={{
                                                                            display: 'inline-block',
                                                                            padding: 10,
                                                                            border: '1px solid #ddd',
                                                                            borderRadius: 8,
                                                                            textDecoration: 'none'
                                                                        }}
                                                                    >
                                                                        📄 {originalName}
                                                                    </a>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        })()}
                                </Card>
                            </>
                        ) : (
                            <>
                                <Descriptions title="Consumption Record" bordered column={1} size="small">
                                    <Descriptions.Item label="Date">{moment(selectedRecord.date).format('DD/MM/YYYY')}</Descriptions.Item>
                                    <Descriptions.Item label="Item">{selectedRecord?.item_name}</Descriptions.Item>
                                    <Descriptions.Item label="Qty">
                                        {selectedRecord.quantity} {selectedRecord.unit}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Unit Cost">KES {selectedRecord.unit_cost?.toFixed(2)}</Descriptions.Item>
                                    <Descriptions.Item label="Total Value">
                                        KES {(selectedRecord.quantity * selectedRecord.unit_cost).toFixed(2)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Purpose">{selectedRecord?.purpose || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="Category">{selectedRecord?.category || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="Recorded By">{selectedRecord?.userName || '-'}</Descriptions.Item>
                                </Descriptions>
                                <Divider />
                                <Title level={5}>Animals</Title>
                                <Tag>{selectedRecord.animal_ids}</Tag>
                                <Tag>{selectedRecord.silo_ids}</Tag>

                                {(selectedRecord?.consumption_type === 'animals' || selectedRecord?.consumption_type === 'mixed') && (
                                    <>
                                        <Divider />
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                marginBottom: 12
                                            }}
                                        >
                                            <Title level={5} style={{ margin: 0 }}>
                                                Animals Consumed To
                                            </Title>
                                            <Badge count={animalIdentifiers.length} showZero color="blue" />
                                        </div>
                                        {identifiersLoading ? (
                                            <div style={{ textAlign: 'center', padding: 20 }}>
                                                <Spin tip="Loading animal details..." />
                                            </div>
                                        ) : animalIdentifiers.length > 0 ? (
                                            <div
                                                style={{
                                                    maxHeight: 200,
                                                    overflowY: 'auto',
                                                    border: '1px solid #f0f0f0',
                                                    borderRadius: 8,
                                                    padding: 12
                                                }}
                                            >
                                                <Space wrap>
                                                    {animalIdentifiers.map((animal, i) => (
                                                        <Tooltip
                                                            key={i}
                                                            title={`ID: ${animal.id}${animal.breed ? `\nBreed: ${animal.breed}` : ''}`}
                                                        >
                                                            <Tag color="blue" closable={false} style={{ cursor: 'pointer' }}>
                                                                {animal.identifier}
                                                                {animal.gender && (animal.gender === 'male' ? ' ♂' : ' ♀')}
                                                            </Tag>
                                                        </Tooltip>
                                                    ))}
                                                </Space>
                                            </div>
                                        ) : (
                                            <div style={{ padding: 12, background: '#fafafa', borderRadius: 8, textAlign: 'center' }}>
                                                <Text type="secondary">No specific animals selected (general consumption)</Text>
                                            </div>
                                        )}
                                    </>
                                )}

                                {(selectedRecord?.consumption_type === 'silos' || selectedRecord?.consumption_type === 'mixed') && (
                                    <>
                                        <Divider />
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                marginBottom: 12
                                            }}
                                        >
                                            <Title level={5} style={{ margin: 0 }}>
                                                Silos Consumed From
                                            </Title>
                                            <Badge count={siloIdentifiers.length} showZero color="orange" />
                                        </div>
                                        {identifiersLoading ? (
                                            <div style={{ textAlign: 'center', padding: 20 }}>
                                                <Spin tip="Loading silo details..." />
                                            </div>
                                        ) : siloIdentifiers.length > 0 ? (
                                            <div
                                                style={{
                                                    maxHeight: 200,
                                                    overflowY: 'auto',
                                                    border: '1px solid #f0f0f0',
                                                    borderRadius: 8,
                                                    padding: 12
                                                }}
                                            >
                                                <Space wrap>
                                                    {siloIdentifiers.map((silo, i) => (
                                                        <Tooltip
                                                            key={i}
                                                            title={`Capacity: ${silo.capacity || 'Unlimited'} | Occupancy: ${
                                                                silo.current_occupancy || 0
                                                            }`}
                                                        >
                                                            <Tag color="orange" closable={false} style={{ cursor: 'pointer' }}>
                                                                {silo.identifier}
                                                                {silo.current_occupancy !== undefined &&
                                                                    ` (${Math.round(
                                                                        (silo.current_occupancy / (silo.capacity || 1)) * 100
                                                                    )}%)`}
                                                            </Tag>
                                                        </Tooltip>
                                                    ))}
                                                </Space>
                                            </div>
                                        ) : (
                                            <div style={{ padding: 12, background: '#fafafa', borderRadius: 8, textAlign: 'center' }}>
                                                <Text type="secondary">No silos selected</Text>
                                            </div>
                                        )}
                                    </>
                                )}

                                {/* Raw Data for Debugging (optional) */}
                                {process.env.NODE_ENV === 'development' && (
                                    <>
                                        <Divider />
                                        <Collapse ghost>
                                            <Collapse.Panel header="Raw Data" key="1">
                                                <pre style={{ fontSize: 11, overflowX: 'auto' }}>
                                                    {JSON.stringify(selectedRecord, null, 2)}
                                                </pre>
                                            </Collapse.Panel>
                                        </Collapse>
                                    </>
                                )}
                            </>
                        )}
                    </>
                )}
            </Drawer>
        </>
    );
};

export default StockManagementPage;
